import type { paths } from "@/api-schema";
import { alphartisApiAxios } from "@/config/axios";
import { useQuery } from "@tanstack/react-query";
import { handlePaginatedParameters } from "../_helpers/handle-parameters";
import { optionsWithDefaults } from "../_helpers/options-with-defaults";
import { Options, UseQueryOptionsNoKeyAndFn } from "../_types";

export const GROUP_MANUAL_MEMBERSHIP_USER_SEARCH_RESOURCE = "group-manual-membership" as const;
export const GROUP_MANUAL_MEMBERSHIP_USER_SEARCH_OPERATION = "user-search" as const;
export const GROUP_MANUAL_MEMBERSHIP_USER_SEARCH_API_PATH =
  `/v1/${GROUP_MANUAL_MEMBERSHIP_USER_SEARCH_RESOURCE}/${GROUP_MANUAL_MEMBERSHIP_USER_SEARCH_OPERATION}` as const;

export type GroupManualMembershipUserSearchParameters =
  paths[typeof GROUP_MANUAL_MEMBERSHIP_USER_SEARCH_API_PATH]["post"]["parameters"]["query"];
export type GroupManualMembershipUserSearchRequestBody =
  paths[typeof GROUP_MANUAL_MEMBERSHIP_USER_SEARCH_API_PATH]["post"]["requestBody"]["content"]["application/json"];
export type GroupManualMembershipUserSearchResponse =
  paths[typeof GROUP_MANUAL_MEMBERSHIP_USER_SEARCH_API_PATH]["post"]["responses"][200]["content"]["application/json"];

export function useGroupManualMembershipUserSearchQuery({
  options,
  enabled,
  queryOptions,
}: {
  options?: Options<GroupManualMembershipUserSearchParameters, GroupManualMembershipUserSearchRequestBody>;
  enabled?: boolean;
  queryOptions?: UseQueryOptionsNoKeyAndFn<
    GroupManualMembershipUserSearchResponse,
    Error,
    GroupManualMembershipUserSearchResponse,
    readonly unknown[]
  >;
} = {}) {
  const optionsWithDefaultsResolved =
    optionsWithDefaults<Options<GroupManualMembershipUserSearchParameters, GroupManualMembershipUserSearchRequestBody>>(
      options,
    );

  const requestParameters = {
    ...(optionsWithDefaultsResolved.parameters || {}),
    ...handlePaginatedParameters(optionsWithDefaultsResolved.parameters),
  };

  return useQuery({
    queryKey: [
      GROUP_MANUAL_MEMBERSHIP_USER_SEARCH_RESOURCE,
      GROUP_MANUAL_MEMBERSHIP_USER_SEARCH_OPERATION,
      { parameters: requestParameters, body: optionsWithDefaultsResolved.body },
    ],
    queryFn: async () => {
      const response = await alphartisApiAxios.post<GroupManualMembershipUserSearchResponse>(
        GROUP_MANUAL_MEMBERSHIP_USER_SEARCH_API_PATH,
        optionsWithDefaultsResolved.body,
        { params: requestParameters },
      );

      return response.data;
    },
    ...queryOptions,
    enabled: (enabled ?? true) && !!optionsWithDefaultsResolved.body?.group,
  });
}
